import angular from "angular";

import {
  AccountingTargets,
  CalcRowConfig,
  CalcRowsLogic,
  Calculation,
  Calculations,
  CalculationRowType,
  CalculatorLogic,
  Dates,
  EnumerationsLogic,
  IncomeTypesLogic,
  Numeric,
  RowsUsecaseLogic,
  UserDefinedRow,
  YearlyChangingNumbers,
  Years,
  IncomeTypeMetadata,
  BaseUsecaseLogic,
  InputEnumOption,
  MilageOwnCarUsecaseLogic,
  Translations,
  UserDefinedRowLogic,
  CalculationRowUnit,
  WorktimeImportRow,
  Arrays,
  CostAccountingDimensionDefinition,
  CostAccountingDimensionScope,
  CalculationRowSource
} from "@salaxy/core";

import {
  EditDialogParameters,
  RowTypeService,
  SessionService,
  UiHelpers,
} from "../../services";

import { ListControllerBase, ListControllerBaseBindings } from "../bases";

class CalcRows2019ControllerBindings extends ListControllerBaseBindings {
  /** Sets the title in the table header. */
  public title = "@";

  /** Shows edit buttons. Used in Payroll. */
  public showEditButtons = "<";

  /** Shows gross totals and total payment in table footer */
  public showGrossTotals = "<";

  /**
   * Alternative binding for parent: Give just a stand-alone rows list.
   * Setting the row to an array will set these rows to the parent component
   * or create a new parent component if no parent component is specified.
   */
  public rows = "<";

  /** If set, filters the rows based on categories (plus rowTypes if set) */
  public categories = "<";

  /** If set, filters the rows based on statuses (Worktime rows only) */
  public statuses = "<";

  /**
   * Defines the helper texts and branding color for row type selection list dialog.
   * Null for default "primary" (no texts)
   */
  public rowTypeSelectHelp = "@";

  /** If set, shows only these types (plus categories if set) */
  public rowTypes = "<";

  /**
   * If set, shows only these sources in the source drop-down.
   * This is a comma separated list of enum values.
   */
  public rowSources = "@";

  /**
   * Optional CostAccountingDimensionDefinitions
   * If not set, cost accounting dimensions and their options are read from the settings.
   */
  public dimensions = "<";

  /**
   * User interface type of the editor: "default" is the one used in calculators.
   * "worktime-import" is used in editing worktime import (contains employment relation).
   * "worktime-import-employment" is used in editing worktime import (no employment, contains period).
   * "salary-defaults" is used behind empoloyment to specify defaults (also in the settings).
   */
  public type = "@";

  /** Hides final cost row. */
  public hideFinalCostRow = "<";

  /**
   * Propertieds and logic for the situation where the dialog is dispalyed as part of row type definition editor.
   */
  public definitionEditor = "<";

  /** Default dimension id to show in rows editor */
  public defaultDimension = "@";

  /** Count of dimension to show in editor */
  public dimensionCount = "<";

  /** In supported modes, shows the rows selection checkboxes */
  public showRowSelection = "<";
}

/**
 * Editor for Calculation rows / usecases.
 * Can be bound to a Calculation or to Employment relation.
 */
export class CalcRows2019Controller extends ListControllerBase<
  Calculation,
  WorktimeImportRow
> {
  /** Bindings for components that use this controller */
  public static bindings = new CalcRows2019ControllerBindings();

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "UiHelpers",
    "$timeout",
    "SessionService",
    "EditDialogParameters",
    "$element",
    "RowTypeService",
    "AccountingTargets",
    "Calculations"
  ];

  /** New empty row for databinding when creating a new row. */
  public newRow: UserDefinedRow;

  /** Currently selected row in 2022 UI. */
  public current: UserDefinedRow;

  /** Title in the Table header */
  public title: string;

  /** Shows edit buttons. Used in Payroll. */
  public showEditButtons: boolean;

  /** Shows gross totals and total payment in table footer */
  public showGrossTotals: boolean;

  /**
   * Alternative binding for parent: Give just a stand-alone rows list.
   * Setting the row to an array will set these rows to the parent component
   * or create a new parent component if no parent component is specified.
   * The resolving occures in list get, so the order of setting is not an issue.
   */
  public rows: UserDefinedRow[];

  /**
   * Propertieds and logic for the situation where the dialog is displayed as part of row type definition editor.
   * If undefined, this is a rows editor (typically for a calculation) and not a row type definition editor.
   */
  public definitionEditor?: {
    /** Mode of the definition editor. */
    mode: "builtIn" | "custom" | "prices";
  };

  /**
   * If set, filters the rows based on categories (plus rowTypes if set).
   * Note that category error (validation errors) is added later and may not be supported by all parts of the UI.
   */
  public categories: (
    | "salary"
    | "salaryCalc"
    | "holidays"
    | "salaryAdditions"
    | "benefits"
    | "expenses"
    | "deductions"
    | "other"
  )[];

  /**
   * Defines the helper texts and branding color for row type selection list dialog
   * (showRowTypeSelectionDialog method)
   */
  public rowTypeSelectHelp: "salaries" | "benefits" | "expenses" | "deductions";

  /** If set, shows only these types (plus categories if set) */
  public rowTypes: CalculationRowType[];

  /** If set, shows only the rows with these statuses. */
  public statuses: string[] | string;

  /**
   * If set, shows only these sources in the source drop-down.
   * This is a comma separated list of enum values.
   */
  public rowSources: string;

  /** User interface type of the editor: Default is the one used in calculators. */
  public type: "worktime-import" | "worktime-import-employment" | "salary-defaults" | "default";

  /** If true, the current UI is a modal dialog. */
  public isDialog: boolean;

  /** Information on the usecase specific data / UI for this row. */
  public usecase: {
    isLoading?: boolean;
    schema?: any;
    isDefined?: boolean;
  } = {};

  /** Hides final cost row. */
  public hideFinalCostRow = false;

  /** Default dimension to show in rows editor */
  public defaultDimension: string;

  /** Count of dimension to show in editor */
  public dimensionCount = 0;

  public _dimensions: CostAccountingDimensionDefinition[] = undefined;

  private _vehicleTypeSelection: InputEnumOption[];
  private _vehicleAdditions: InputEnumOption[];

  private _dailyAllowanceRegions: InputEnumOption[];

  /**
   * Creates a new CalcRows2019Controller
   * @param uiHelpers - Salaxy UI helpers service.
   * @ignore
   */
  constructor(
    uiHelpers: UiHelpers,
    private $timeout: angular.ITimeoutService,

    private session: SessionService,
    editDialogParams: EditDialogParameters<any>,
    private $element: JQLite,
    private rowTypeService: RowTypeService,
    private accountingTargets: AccountingTargets,
    private calculations: Calculations
  ) {
    super(uiHelpers);
    if (editDialogParams.logic?.calc) {
      // EditDialogParameters inject controller data in dialog situation.
      // At the moment this is always a single item, but may later be extended to lists.
      this.parent = editDialogParams.logic.calc;
      if (editDialogParams.logic.definitionEditor) {
        this.definitionEditor = editDialogParams.logic.definitionEditor;
        this.isDialog = false;
      } else {
        this.isDialog = true;
      }
      this.current = editDialogParams.current;
    }
  }

  /** Sets the default values in init. */
  public $onInit() {
    this.newRow = this.getBlank();
  }

  /** Yearlysidecosts for translations, used in template */
  public get yearlySideCosts(): {
    current: YearlyChangingNumbers;
    previous: YearlyChangingNumbers;
  } {
    const currentYear = Dates.getToday();
    const previousYear = Dates.add(currentYear, -1, "year");
    return {
      current: this.formatYearlyNumbers(
        Years.getYearlyChangingNumbers(currentYear)
      ),
      previous: this.formatYearlyNumbers(
        Years.getYearlyChangingNumbers(previousYear)
      ),
    };
  }

  /**
   * Marks that the editor is stand-alone, meaning that it does not edit a calculation,
   * but it just edits a set of rows, typically in employment or settings.
   * In this case the editor has creaed a fake calculation (because calculation is currently required for the row editor to work).
   * TODO: Consider separating Calculation rows editor from stand-alone row editor.
   */
  public get isStandAlone(): boolean {
    return !!this.parent?.usecase?.data?.standAloneRows;
  }

  /** Copies the given text to clipboard. */
  public copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
  }

  /** List of rows  */
  public get list(): WorktimeImportRow[] | null {
    if (!this.parent) {
      if (this.rows) {
        this.parent = CalculatorLogic.getBlank();
        this.parent.usecase.data.standAloneRows = true;
        this.parent.isReadOnly = this.mode == "read-only";
      } else {
        return null;
      }
    }
    // HACK: This does not belong here: Move to server-side (in v03 release candidate).
    CalculatorLogic.moveSalaryToRows(this.parent);
    if (this.rows && this.parent.rows !== this.rows) {
      // Rows has been set outside.
      this.parent.rows = this.rows;
    }
    return this.parent.rows;
  }

  /** List filtered by categories and rowTypes. */
  public get filteredList(): WorktimeImportRow[] | null {
    const typesToShow = this.rowTypesToShow.map((x) => x as string);
    const statuses = Arrays.assureArray(this.statuses);
    const rowSources = Arrays.assureArray(this.rowSources);
    if (typesToShow.length === 0 && statuses.length === 0 && rowSources.length === 0) {
      return this.list;
    }
    return this.list.filter((x) =>
      (!typesToShow.length || typesToShow.indexOf(x.rowType) >= 0) &&
      (!statuses.length || statuses.indexOf((x as any).status) >= 0) &&
      // HACK: rowSources property should be completely removed when moving default row display to RowTypeSettings editor
      (!rowSources.length || x.source != CalculationRowSource.PriceEmployment)
    );
  }

  /** Combines together categories and rowTypes properties as one list of row types. */
  public get rowTypesToShow() {
    const rowsLogic = new CalcRowsLogic(
      this.session.isInRole("household") ? "household" : "company"
    );
    const types = [...(this.rowTypes || [])];
    if (this.categories) {
      for (const cat of this.categories) {
        types.push(...rowsLogic.getRowTypesByCategory(cat as any));
      }
    }
    return types;
  }

  /** Creating of a new item. */
  public getBlank(): UserDefinedRow {
    const newRow = UserDefinedRowLogic.getBlank(this.list?.filter((x) => x.rowIndex >= 0).length || 0);
    return newRow;
  }

  /**
   * Returns a validation message for a row or null if none is required.
   * @param row The row that is validated.
   */
  public getValidation(row: UserDefinedRow): {
    /** Validation message */
    msg: string;
    /** Type of validation message */
    type: "default" | "error";
  } | undefined {
    return RowsUsecaseLogic.getValidation(row) as { msg: string; type: "default" | "error"; } | undefined;
  }

  /** Template for edit UI that is shown in a modal dialog. */
  public getEditDialogTemplateUrl() {
    return "salaxy-components/calc/rows/CalcRows2019EditDialog.html";
  }

  /**
   * Shows whether accounting editing is enabled
   * @returns true if accounting editing is enabled
   */
  public showAccountingEditing(): boolean {
    return ![
      CalculationRowType.AbsencePeriod,
      CalculationRowType.BicycleBenefit,
      CalculationRowType.CarBenefit,
      CalculationRowType.MealBenefit,
      CalculationRowType.SubsidisedCommute,
      CalculationRowType.HourlySalaryWithWorkingTimeCompensation,
      CalculationRowType.TotalWorkerPayment,
      CalculationRowType.TaxAtSource,
      CalculationRowType.TaxWithholding].includes(this.current.rowType);
  }

  /**
   * Opens the accounting ruleset selection dialog
   * @param isDebit - Whether to set debit account (true) or credit account (false)
   * @returns Promise that resolves when the dialog is closed
   */
  public async openAccountingRulesetSelection(isDebit: boolean): Promise<void> {
    try {
      const targetOdata = (await this.accountingTargets.getOData({$filter: `id eq '${this.session.session?.settings?.accountingTarget.id}'`})).items[0];
      if(!targetOdata) {
        this.uiHelpers.showConfirm("SALAXY.NG1.CalcRowsComponent.editDialog.accounting.entry.rulesetSelection.error", "SALAXY.NG1.CalcRowsComponent.editDialog.accounting.entry.rulesetSelection.targetCoANotFound");
        return;
      }

      const target = await this.accountingTargets.getSingle(`${this.session.session?.settings?.accountingTarget.id}@${targetOdata.owner}`);
      if(!target) {
        this.uiHelpers.showConfirm("SALAXY.NG1.CalcRowsComponent.editDialog.accounting.entry.rulesetSelection.error", "SALAXY.NG1.CalcRowsComponent.editDialog.accounting.entry.rulesetSelection.targetCoANotFound");
        return;
      }

      const logic = { title: "SALAXY.NG1.CalcRowsComponent.editDialog.accounting.entry.rulesetSelection.selectTargetCoA", items: target.ruleSet.accounts };
      const res = await this.uiHelpers.openEditDialog("salaxy-components/modals/calc/SelectRuleset.html", null, logic);

      if(res.result?.account) {
        this.current.accounting ??= {};
        (this.current.accounting as any).entry ??= {};
        (this.current.accounting as any).entry[isDebit ? "debitAccountNumber" : "creditAccountNumber"] = res.result.account;
      }
    } catch(error) {
      console.error(error);
      this.uiHelpers.showConfirm("SALAXY.NG1.CalcRowsComponent.editDialog.accounting.entry.rulesetSelection.error", "SALAXY.NG1.CalcRowsComponent.editDialog.accounting.entry.rulesetSelection.targetCoANotFound");
    }
  }
  /**
   * Shows a preview of the accounting report for the currently selected row.
   * Creates a calculation with only the current row, recalculates it, and opens
   * a dialog to display the accounting report query.
   * @returns Promise that resolves when the dialog is closed
   * @throws {Error} If no current row is selected or if recalculation fails
   * @example
   * // Show accounting preview for the current row
   * await this.showRowAccountingPreview();
   */
  public async showRowAccountingPreview(): Promise<void> {
    try {
      let calc = angular.copy(this.parent);
      const currentRow = calc.rows.find((x) => x.rowIndex === this.current.rowIndex);
      if(currentRow) {
        currentRow.accounting ??= {};
        (currentRow.accounting as any).entry = (this.current.accounting as any).entry;
      }
      calc = await this.calculations.recalculate(calc);

      // Prepare the logic object for the dialog
      const logic = {
        title: "SALAXY.NG1.CalcRowsComponent.editDialog.accounting.entry.rowAccountingPreview.targetCoA",
        lang: Translations.getLanguage(),
        calcs: [calc],
        view: "partial"
      };

      // Open the accounting report query dialog
      await this.uiHelpers.openEditDialog(
        "salaxy-components/modals/calc/CalcAccountingReportQuery.html",
        null,
        logic,
        "xl"
      );
    } catch (error) {
      console.error("Error showing row accounting preview:", error);
      this.uiHelpers.showConfirm("SALAXY.NG1.CalcRowsComponent.editDialog.accounting.entry.rowAccountingPreview.error", "SALAXY.NG1.CalcRowsComponent.editDialog.accounting.entry.rowAccountingPreview.accountingPreviewFailed");
      throw error;
    }
  }

  /** checks if the image fileUrl given in the cost line is not a blob (omapalkka image upload adds blob) */
  public showImagePreview(): boolean {
    return (
      this.current?.data?.fileUrl &&
      !this.current?.data?.fileUrl.startsWith("blob:")
    );
  }

  /**
   * Gets the UX configuration for a row.
   * @param row Row for which the config is fetched.
   * If not set, gets the config for current.
   */
  public getConfig(row: UserDefinedRow = null): CalcRowConfig | undefined {
    return this.rowTypeService.getConfig(row || this.current);
  }

  /**
   * Gets the placeholder text for an input.
   */
  public getPlaceholderText(row: UserDefinedRow, field: "amount" | "price") {
    if (!row.rowType) {
      return "";
    }
    const config = this.getConfig(row);
    if (field === "amount") {
      return config.amount.label;
    } else {
      return config.price.label;
    }
  }

  /**
   * Return the total for the row
   */
  public getRowTotal(row: UserDefinedRow) {
    if (row == null) {
      return null;
    }
    if (row.count == null) {
      return row.price;
    } else {
      return row.count * row.price;
    }
  }

  /**
   * Returns true, if the row is disabled. The row is interpreted as disabled if it has no rowType
   * or it has been set as hidden in the configuration.
   * @param row Row to check
   * @param field Type of the input / field.
   */
  public isDisabled(row: UserDefinedRow, field: "amount" | "price") {
    if (!row?.rowType) {
      return true;
    }
    const config = this.getConfig(row);
    if (!config?.amount || !config?.price) {
      console.debug("Row config not found for row", row);
      return false;
    }
    switch (field) {
      case "amount":
        return config.amount.input === "hidden";
      case "price":
        return config.price.input === "hidden";
      default:
        return false;
    }
  }

  /**
   * Updates the compensation row with the calculated price based on the unit type.
   * @param row - The row containing both the main row price and compensation row data.
   */
  public updateCompensationRow(row: UserDefinedRow): void {
    const compRow = row.data.compensationRows[0];
    let price = 0;
    switch (compRow.unit) {
      case "percent":
        price = row.price * compRow.count;
        break;
      case "hours":
        price = compRow.price;
        break;
    }
    compRow.price = price;
  }

  /** Row type changes on a row */
  public rowTypeChanged(row: UserDefinedRow) {
    this.rowTypeService.applyDefaultsToNewRow(row, this.parent?.workflow?.salaryDate ?? "today");
  }

  /**
   * Commits the new row in newRow property and adds it to the rows list.
   * Updates usecase and sets the newRow as new blank row.
   */
  public commitNewRow(showEdit = false): UserDefinedRow {
    if (!this.newRow?.rowType) {
      console.error("No row type set for the new row.");
      return null;
    }
    const row = this.newRow;
    this.rowTypeChanged(row);
    this.updateUsecase(row);
    this.list.push(row);
    this.$timeout(() => {
      // Reset new row type after timeout to avoid debounce
      this.newRow = this.getBlank();
    }, 300);
    if (showEdit) {
      this.showEdit(row, "auto");
    } else {
      this.$timeout(() => {
        let elem: HTMLElement = null;
        if (this.isDisabled(row, "amount")) {
          elem = (this.$element[0] as HTMLElement).querySelector(
            "#iptPrice" + row.rowIndex
          );
        } else {
          elem = (this.$element[0] as HTMLElement).querySelector(
            "#iptCount" + row.rowIndex
          );
        }
        if (elem) {
          elem.focus();
        }
      }, 500);
    }
    return row;
  }

  /** Shows the selection dialog for the row type. */
  public showRowTypeSelectionDialog() {
    this.uiHelpers
      .openEditDialog(
        "salaxy-components/modals/calc/row-type-select.html",
        this.newRow,
        {
          categories: this.categories,
          rowTypes: this.rowTypes,
          rowTypeSelectHelp: this.rowTypeSelectHelp,
        }
      )
      .then((result) => {
        if (result.action === "ok" && result.hasChanges) {
          this.showEdit(this.commitNewRow(), "auto");
        }
      });
  }

  /** Updates the usecase property values without going to server. */
  public updateUsecase(row: UserDefinedRow) {
    this.getUsecaseLogic(row);
  }

  /**
   * Returns the usecase logic instance for current row, also running the first update cycle on the row.
   */
  public getUsecaseLogic(row: UserDefinedRow): BaseUsecaseLogic {
    return RowsUsecaseLogic.getUsecaseLogicForCalc(row, this.parent, true);
  }

  /**
   * Gets vehicle type selections from usecase
   * @returns InputEnumOption[] of vehicle selections
   */
  public getVehicleTypeSelection(): InputEnumOption[] {
    if (!this._vehicleTypeSelection) {
      this._vehicleTypeSelection = [];
      const vehicleTypes: InputEnumOption[] = (
        RowsUsecaseLogic.getUsecaseLogicForCalc(
          this.current,
          this.parent,
          true
        ) as MilageOwnCarUsecaseLogic
      ).getVehicleTypeSelection();
      this._vehicleTypeSelection.push(...vehicleTypes);
    }
    return this._vehicleTypeSelection;
  }

  /**
   * Gets vehicle additions from usecase
   * @returns InputEnumOption[] of vehicle additions
   */
  public getVehicleAdditions(): InputEnumOption[] {
    if (!this._vehicleAdditions) {
      this._vehicleAdditions = [];
      const vehicleAdditions: InputEnumOption[] = (
        RowsUsecaseLogic.getUsecaseLogicForCalc(
          this.current,
          this.parent,
          true
        ) as MilageOwnCarUsecaseLogic
      ).getAdditionsSelection();
      this._vehicleAdditions.push(...vehicleAdditions);
    }
    return this._vehicleAdditions;
  }

  /**
   * Gets the regions for InternationalDailyAllowance as of today
   */
  public get dailyAllowanceRegions(): InputEnumOption[] {
    if (!this._dailyAllowanceRegions) {
      this._dailyAllowanceRegions = [];
      const result = [];
      Object.entries(Years.getYearlyTaxFreeNumbers("today").countries).forEach(
        (x) => {
          result.push({
            text: `${Translations.get(
              `SALAXY.CORE.yearlyTaxfreeYears.countries.${x[0]}`
            )} (${x[1]}€)`,
            value: (x[0] as any) ?? "",
          });
        }
      );
      this._dailyAllowanceRegions.push(...result);
    }
    this._dailyAllowanceRegions = this._dailyAllowanceRegions.sort((a, b) =>
      a.text.localeCompare(b.text)
    );
    return this._dailyAllowanceRegions;
  }

  /**
   * Gets the Incomes Register metadata for a calculation row.
   * @param row Row to check, null for current row.
   * @returns The income type metadata. Empty object for empty row or empty irData.
   */
  public getIrCodeData(row: UserDefinedRow = null) {
    row = row || this.current;
    if (row?.data?.irData) {
      return IncomeTypesLogic.getAll().find(
        (x) => x.code === row.data.irData.code
      );
    }
    return {};
  }

  /** Return the metadata based on irCode */
  public getIrCodeDataWithCode(irCode: number): IncomeTypeMetadata {
    return IncomeTypesLogic.getAll().find((x) => x.code === irCode);
  }

  /** Returns true if the filtered list has any rows. */
  public get hasRows(): boolean {
    return (this.filteredList || []).length > 0;
  }

  /** Gets the total for the current rows. */
  public getTotal() {
    return (this.filteredList || []).reduce(
      (prev, curr) => prev + curr.count * curr.price,
      0
    );
  }
  /**
   * Gets the gross total for the calculation.
   * Current implementation does not support filtering.
   */
  public getGrossTotal() {
    if (!this.parent || !this.parent.result || !this.parent.result.totals) {
      return null;
    }
    return this.parent.result.totals.totalGrossSalary;
  }

  /**
   * Gets the total payment for the calculation.
   * Current implementation does not support filtering.
   */
  public getFinalCost() {
    if (
      !this.parent ||
      !this.parent.result ||
      !this.parent.result.employerCalc
    ) {
      return null;
    }
    return this.parent.result.employerCalc.finalCost;
  }
  /**
   * Gets the net salary for the calculation.
   * Current implementation does not support filtering.
   */
  public getNetSalary() {
    return this.parent.result.workerCalc.salaryPayment;
  }

  /**
   * Gets the total payment that is paid to palkkaus.fi.
   * Current implementation does not support filtering.
   */
  public getTotalPayment() {
    return this.parent.result.employerCalc.totalPayment;
  }

  /**
   * Closes the edit dialog (either modal dialog or inline).
   * @param method The closing method.
   * @param $close In the case of modal dialog, this is the modal dialog close method.
   */
  public closeEditDialog(
    method: "cancel" | "ok-no-save" | "delete-no-save",
    $close: any = null
  ): void {
    if ($close) {
      $close(method);
      return;
    }
    switch (method) {
      case "delete-no-save":
        this.delete(this.current);
        this.current = null;
        return;
      case "ok-no-save": // Perhaps later have commit functionality.
      case "cancel":
      default:
        this.current = null;
        return;
    }
  }

  /**
   * Shows the editor UI that may either be a dialog or an inline edit UI.
   * @param item Item to edit. If rowIndex is null/undefined, the item is considered a new row and the properties
   * extend new item (fetched from getBlank()).
   * @param type Type of the edit UI.
   */
  public showEdit(
    item: UserDefinedRow,
    type: "dialog" | "inline" | "auto" = "auto"
  ) {
    if (!item) {
      throw new Error("showEditDialog called without a parameter.");
    }
    const isNew = this.isInEdit && item.rowIndex == null;
    if (isNew) {
      item = angular.merge({}, this.getBlank(), item);
      this.rowTypeChanged(item);
      this.updateUsecase(item);
    } else {
      item.data = item.data ?? {};
      item.accounting = item.accounting ?? {};
    }

    if (type != "dialog" && type != "inline") {
      const elem = this.$element[0]?.querySelector(
        ".salaxy-calc-rows-editor-details-container"
      ) as HTMLElement;
      if (
        elem &&
        (elem.offsetWidth || elem.offsetHeight || elem.getClientRects().length)
      ) {
        // jQuery isVisible: https://stackoverflow.com/questions/19669786/check-if-element-is-visible-in-dom
        type = "inline";
      } else {
        type = "dialog";
      }
    }
    if (type == "dialog") {
      this.showEditDialog(item, isNew);
      return;
    }

    // Inline edit UI.
    if (this.current == item) {
      this.current = null;
    } else {
      if (isNew) {
        this.list.push(item);
      }
      this.current = item;
      this.$timeout(() => {
        const elem: HTMLElement = (
          this.$element[0] as HTMLElement
        ).querySelector(".salaxy-calc-details-dialog .form-control");
        if (elem) {
          elem.focus();
        }
      }, 300);
    }
  }

  /**
   * Shows the edit dialog: Extends the base functionality with custom controller (non-supported results removed).
   * @param item Item to edit or string "new" for creating a new one.
   * @param isNew Optional way of specifying that the item is a new item (with default values, not yet added to the list).
   * If item is string "new", this parameter will have no effect (will always be true).
   */
  public showEditDialog(item: UserDefinedRow | "new", isNew = false) {
    if (!item) {
      throw new Error("showEditDialog called without a parameter.");
    }
    if (item === "new") {
      isNew = true;
      item = this.getBlank();
    }

    item.data = item.data ?? {};
    item.accounting = item.accounting ?? {};
    this.uiHelpers
      .openEditDialog(
        this.getEditDialogTemplateUrl(),
        item,
        {
          calc: this.parent,
          readOnly: !this.isInEdit,
        },
        "",
        "CalcRows2019Controller"
      )
      .then((result) => {
        if (result.action === "ok-no-save") {
          if (isNew) {
            this.list.push(result.item);
          }
        } else if (result.action === "delete-no-save") {
          if (!isNew) {
            this.delete(item as UserDefinedRow);
          }
        } else {
          // No changes, cancel
        }
      });
  }

  /**
   * Gets or sets the flag telling whether the component is in edit or read-only mode.
   */
  public get isInEdit(): boolean {
    if (!this.isEditable) {
      return !!this._isInEdit;
    }
    return super.isInEdit;
  }
  public set isInEdit(value: boolean) {
    super.isInEdit = value;
  }

  /**
   * Changes the view from read-only mode to edit mode.
   */
  public startEdit() {
    if (!this.isEditable) {
      this.isInEdit = true;
      this.onStartEdit({
        parent: this.parent,
      });
    } else {
      super.startEdit();
    }
  }

  /**
   * Returns placeholder text for row
   * @param row used row
   * @returns row message as string
   */
  public getMessagePlaceholderText(row: UserDefinedRow): string {
    if (row.message) {
      return row.message;
    }
    let label = "";
    if (row.rowType === CalculationRowType.IrIncomeType) {
      label = IncomeTypesLogic.getAll().find(
        (x) =>
          (row.data?.kind && x.transactionCode === row.data.kind) ||
          (row.data?.irData?.code > 0 && x.code === row.data.irData.code)
      )?.label;
    } else if (row.rowType == CalculationRowType.AbsencePeriod) {
      label = EnumerationsLogic.getEnumLabel(
        "AbsenceCauseCode",
        row.data?.kind
      );
    } else if (
      [
        CalculationRowType.DailyAllowance,
        CalculationRowType.EmploymentTermination,
        CalculationRowType.WorkingTimeCompensation,
        CalculationRowType.Remuneration,
        CalculationRowType.OtherCompensation,
        CalculationRowType.NonProfitOrg,
        CalculationRowType.MealBenefit,
        CalculationRowType.Training,
        CalculationRowType.PaidSickLeave,
        CalculationRowType.TotalWorkerPayment,
        CalculationRowType.TimeRatePay,
      ].includes(row.rowType) &&
      row.data?.kind
    ) {
      label = EnumerationsLogic.getEnumLabel(
        `${row.rowType}Kind`,
        row.data.kind
      );
    }

    return (
      label || EnumerationsLogic.getEnumLabel("CalculationRowType", row.rowType)
    );
  }

  /** Initializes and returns the unit for the row. */
  public ensureUnit(row: UserDefinedRow): CalculationRowUnit {
    if (!row.unit) {
      const config = this.getConfig(row);
      row.unit = config.amount.unit;
    }
    return row.unit;
  }

  /**
   * Select a row in the list or unselect if already selected.
   * @param row Row to select or "all" to select all currently vivisble rows.
   * @param value Optional value to force selection true or false regardless of the current state.
   */
  public toggleSelectedRow(row: WorktimeImportRow | "all", value?: boolean) {
    if (row == "all") {
      this.filteredList.forEach((x) => {
        this.toggleSelectedRow(x, value);
      });
      return;
    }
    if (row.data.isSelected && !value) {
      // Toggle / Force unselect (undefined/false), Force select (true) keeps as is.
      row.data.isSelected = false;
    } else if (value !== false) {
      // Toggle or Force select (undefined/true). Force unselect (false) keeps as is.
      row.data.isSelected = true;
    }
  }

  /**
   * Gets the selected rows in the list (only the filtered / visible rows).
   * @returns The selected rows.
   */
  public getSelectedRows(): WorktimeImportRow[] {
    return this.filteredList.filter((x) => x.data.isSelected);
  }

  /**
   * Determines the visibility of dimensions based on certain conditions.
   * @returns {boolean} - Returns `true` if dimensions should be visible, otherwise `false`.
   */
  public showDimension(): boolean {
    return this.dimensionsCountToShow() > 0;
  }

  /**
   * Returns the number of dimensions to show.
   * @returns {number} The number of dimensions to show. If `dimensionCount` is not defined, returns 0.
   */
  public dimensionsCountToShow(): number {
    return this.getDimensionsToShow().length;
  }

  /**
   * Retrieves the dimensions to show based on the session settings and predefined rules.
   * This method filters the available dimensions to exclude hidden ones and includes
   * those that match specific scope criteria. It prioritizes adding the default dimension
   * and cost center first, followed by the remaining dimensions until the limit defined
   * by `dimensionsCountToShow` is reached.
   * @returns {CostAccountingDimensionDefinition[]} An array of dimensions to be displayed.
   */
  public getDimensionsToShow(): CostAccountingDimensionDefinition[] {
    if (!this._dimensions) {
      const dimensions = (this.session.session?.settings?.costAccounting?.dimensions ?? [])
      .filter( (x) =>
        // no hidden scope
        x.scope !== CostAccountingDimensionScope.Hidden &&
        (
          // show if scope definition for dimension not given
          ( !x.scope || x.scope === CostAccountingDimensionScope.None) ||
          // or this scope
          (CostAccountingDimensionScope.Row === x.scope)
        )
      );
      const defaultDimension = this.defaultDimension || "costCenter";
      // take a copy
      const ordered = dimensions.slice(0);
      // sort and mutate
      ordered.sort( (x, y) => x.id == defaultDimension ? -1 : x.id == "costCenter" && y.id != defaultDimension ? -1: 0  );
      // set length
      ordered.length = (ordered.length > (this.dimensionCount ?? 0)) ? this.dimensionCount ?? 0: ordered.length;
      return this._dimensions = ordered;
    }
    return this._dimensions;
  }

  private formatYearlyNumbers(
    yearlyNumbers: YearlyChangingNumbers
  ): YearlyChangingNumbers {
    const keys = Object.keys(yearlyNumbers.sideCosts);
    for (const key of keys) {
      if (yearlyNumbers.sideCosts[key] >= 1) {
        yearlyNumbers.sideCosts[key + "Formatted"] = Numeric.formatPrice(
          yearlyNumbers.sideCosts[key]
        );
      }
    }
    return yearlyNumbers;
  }
}
